import React, { useState } from 'react';
import {
  VStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Select,
  useColorModeValue,
  FormErrorMessage,
  SimpleGrid,
} from '@chakra-ui/react';
import { useOnboardingStore } from '../../../stores/onboardingStore';

const BasicInfoStep: React.FC = () => {
  const { userData, updateUserData, currentStep } = useOnboardingStore();
  
  const textColor = useColorModeValue('gray.600', 'gray.300');
  // Simple validation check for displaying errors
  const hasAttemptedSubmit = false; // This would need to be tracked if needed

  // State for date input as text and validation
  const [dateInput, setDateInput] = useState(() => {
    if (userData.birthday) {
      const d = new Date(userData.birthday);
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const year = d.getFullYear();
      return `${month}/${day}/${year}`;
    }
    return '';
  });
  const [dateError, setDateError] = useState('');

  const validateDate = (month: number, day: number, year: number): string | null => {
    // Check month range
    if (month < 1 || month > 12) {
      return 'Invalid month (must be 01-12)';
    }

    // Check year range (not too old, not in future)
    const currentYear = new Date().getFullYear();
    const minYear = currentYear - 120; // Max age 120 years
    const maxYear = currentYear - 5; // Min age 5 years (reasonable for education platform)
    
    if (year < minYear) {
      return `Year cannot be before ${minYear}`;
    }
    if (year > currentYear) {
      return 'Date cannot be in the future';
    }
    if (year > maxYear) {
      return `Must be at least 5 years old`;
    }

    // Check day range based on month
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day < 1 || day > daysInMonth) {
      return `Invalid day for this month (must be 01-${daysInMonth})`;
    }

    // Create date and validate it's not in future
    const date = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time for accurate comparison
    
    if (date > today) {
      return 'Date cannot be in the future';
    }

    return null; // No error
  };

  const handleDateInputChange = (value: string) => {
    // Remove all non-numeric characters
    const numbersOnly = value.replace(/\D/g, '');
    
    // Format the input with slashes
    let formatted = '';
    
    if (numbersOnly.length > 0) {
      // Add month
      formatted = numbersOnly.slice(0, 2);
      
      // Add slash and day if we have more than 2 digits
      if (numbersOnly.length > 2) {
        formatted += '/' + numbersOnly.slice(2, 4);
      }
      
      // Add slash and year if we have more than 4 digits
      if (numbersOnly.length > 4) {
        formatted += '/' + numbersOnly.slice(4, 8);
      }
    }
    
    setDateInput(formatted);
    setDateError(''); // Clear error while typing
    
    // Try to parse and validate the date if it looks complete
    const parts = formatted.split('/');
    if (parts.length === 3) {
      const [monthStr, dayStr, yearStr] = parts;
      const month = parseInt(monthStr);
      const day = parseInt(dayStr);
      const year = parseInt(yearStr);
      
      // Only validate if we have a complete year (4 digits)
      if (yearStr.length === 4 && !isNaN(month) && !isNaN(day) && !isNaN(year)) {
        const error = validateDate(month, day, year);
        
        if (error) {
          setDateError(error);
          updateUserData('birthday', null);
        } else {
          const date = new Date(year, month - 1, day);
          setDateError('');
          updateUserData('birthday', date);
        }
      } else {
        updateUserData('birthday', null);
      }
    } else {
      updateUserData('birthday', null);
    }
  };


  return (
    <VStack spacing={8} align="stretch" justify="center" maxW="800px" mx="auto" w="full">
        {/* Header */}
        <VStack spacing={4} textAlign="center">
          <Heading size="2xl">Tell us about yourself</Heading>
          <Text color={textColor} fontSize="lg">
            We'll use this information to personalize your learning experience.
          </Text>
        </VStack>

        {/* Form */}
        <VStack spacing={6} align="stretch">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormControl isRequired>
            <FormLabel>First Name</FormLabel>
            <Input
              placeholder="Enter your first name"
              value={userData.firstName}
              onChange={(e) => updateUserData('firstName', e.target.value)}
              maxLength={20}
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Last Name</FormLabel>
            <Input
              placeholder="Enter your last name"
              value={userData.lastName}
              onChange={(e) => updateUserData('lastName', e.target.value)}
              maxLength={20}
            />
          </FormControl>
        </SimpleGrid>

        <FormControl isRequired isInvalid={!!dateError}>
          <FormLabel>Birthday</FormLabel>
          <Input
            type="text"
            placeholder="MM/DD/YYYY"
            value={dateInput}
            onChange={(e) => handleDateInputChange(e.target.value)}
            maxLength={10}
            autoComplete="off"
          />
          {dateError ? (
            <FormErrorMessage>{dateError}</FormErrorMessage>
          ) : (
            <Text fontSize="xs" color={textColor} mt={1}>
              Enter your birthdate as numbers only (slashes added automatically)
            </Text>
          )}
        </FormControl>

        <FormControl>
          <FormLabel>Pronouns (Optional)</FormLabel>
          <Select
            placeholder="Select pronouns"
            value={userData.pronouns || ''}
            onChange={(e) => updateUserData('pronouns', e.target.value)}
          >
            <option value="he/him">He/Him</option>
            <option value="she/her">She/Her</option>
            <option value="they/them">They/Them</option>
            <option value="other">Other</option>
            <option value="prefer-not-to-say">Prefer not to say</option>
          </Select>
        </FormControl>
        </VStack>
      </VStack>
  );
};

export default BasicInfoStep;